/**
 * api/upload.ts —— 图片上传（§五 5.8）
 *
 * ✅ **2026-08-22：COS 已开通**（`camp-dashboard-1349887488`），服务端 `sign` 回 `mode:'cos'`，
 *   走真直传。⚠ **mock 分支仍要留着**：本机开发不配 COS 密钥时照样走它
 *   （不真上传、只在当次页面预览，刷新即无），录入弹窗里那行说明也必须留着。
 *
 * 🔴 **2026-08-22 加了上传前压缩**（`shared/image.ts`）：手机直出 3—5MB 的原图直传，
 *   会让打卡页一屏下行 150MB+ —— 而全站显示尺寸最大只有 355×230。
 *   压缩收在 `uploadFiles()` 这一个出口里，**调用点不需要各自去压**。
 *
 * ⚠ 直传 COS 的那个 PUT 是**跨域到腾讯云**的，不是我们自己的 API，所以它是
 *   `api/client.ts` 之外唯一允许出现的直接请求 —— 带 Cookie 反而会被 COS 拒绝。
 *   🔴 **它用 `XMLHttpRequest` 而不是 `fetch`**：`fetch` **没有上传进度事件**
 *   （要靠 ReadableStream 请求体 + `duplex:'half'`，浏览器支持面还不够）。
 *   传 9 张图要十几秒，没有进度条用户会以为页面卡死——这是本轮修的第二个问题。
 *   ⚠ 别"顺手统一成 fetch"，统一掉的同时进度条就没了。
 */
import { post, type ReqOptions } from './client.js';
import { compressAll } from '../shared/image.js';

export interface SignedItem {
  /** 对象键。⚠ 【不是可访问地址】，读图把它拼进 `imgUrl()` */
  objectKey: string;
  /** 直传地址；mock 模式恒为空串 */
  uploadUrl: string;
}

export interface SignResult {
  /** 'cos' = 真直传；'mock' = bucket 未开通，只做本地预览 */
  mode: 'cos' | 'mock';
  items: SignedItem[];
}

/** 签发上传位（本队权限）。⚠ 一次最多 9 个，与「一条打卡最多 9 张」同一个上限 */
export const signUpload = (teamId: string, count: number, ext: string[], opts?: ReqOptions) =>
  post<SignResult>('/api/upload/sign', { teamId, count, ext }, opts);

/**
 * 图片变体：`c` = 卡片封面（720），`t` = 九宫格缩略（176），`o` = 原图（1600）。
 * ⚠ 口径唯一出处在后端 `modules/upload/adapter.ts`，这里只是把字母拼进地址。
 */
export type ImgVariant = 'c' | 't' | 'o';

/**
 * 读图地址（**纯函数，不发请求**）。
 *
 * 🔴 **2026-08-23 推翻了原来的 `viewUrls()`**：那是个 POST，要多花一个往返才拿到地址，
 *   而且签出来的临时 URL **每次都不一样**，浏览器缓存**一次都命中不了**——
 *   每进一次页面就把所有图重下一遍。现在地址是固定的，服务端给一年的 immutable 缓存，
 *   **第二次进页面一个图片请求都不发**。三条实测理由见后端 `adapter.ts` 的「读图」那一节。
 * ⚠ **不要再引入"先问地址再显示"的写法**，那会把缓存这条命重新弄丢。
 * ⚠ 逐段编码：对象键里的 `/` 是路径分隔符，编成 %2F 服务端就匹配不上通配路由了。
 */
export function imgUrl(objectKey: string, v: ImgVariant = 'c'): string {
  return `/api/img/${objectKey.split('/').map(encodeURIComponent).join('/')}?v=${v}`;
}

/** 上传进度。两个阶段分开报——压缩按【张】、上传按【字节】，混成一个百分比会忽快忽慢 */
export interface UploadProgress {
  phase: 'compress' | 'upload';
  /** 0—1，直接拿去写进度条宽度 */
  ratio: number;
  /** 压缩阶段 = 已完成张数；上传阶段 = 已传字节 */
  done: number;
  /** 压缩阶段 = 总张数；上传阶段 = 总字节 */
  total: number;
}

/**
 * 带上传进度的 PUT。⚠ 只用于直传 COS，见文件头。
 * ⚠ `onBytes` 报的是**这一个文件**的已传字节（不是累计），累加由调用方做——
 *   XHR 的 `loaded` 本来就是单请求口径，在这里先累加会重复计数。
 */
function putWithProgress(
  url: string,
  file: File,
  onBytes: (loaded: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    // ⚠ 不带 credentials：带了会被 COS 的签名校验拒掉（与原 fetch 实现一致）
    xhr.withCredentials = false;
    xhr.setRequestHeader('Content-Type', file.type || 'image/jpeg');
    xhr.upload.onprogress = (e) => onBytes(e.loaded);
    xhr.onload = () => {
      // 传完把这个文件记满：`onprogress` 最后一拍不保证等于 total，进度条会永远差一点
      onBytes(file.size);
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`图片上传失败（HTTP ${xhr.status}）`));
    };
    xhr.onerror = () => reject(new Error('图片上传失败（网络中断）'));
    xhr.onabort = () => reject(new Error('图片上传已取消'));
    xhr.send(file);
  });
}

/**
 * 把一批文件传上去，返回对象键（顺序与入参一致）。
 *
 * 🔴 **先压缩再签发**：压完扩展名一律变 `jpg`，所以 `ext` 必须用**压缩后**的文件名算——
 *   拿原文件名去签，对象键会写成 `.heic` / `.png`，而桶里躺的其实是 JPEG。
 * ⚠ 返回里带上 `files`（压缩后的），调用方**用它做本地预览**：
 *   用原 File 建 blob URL 等于把 3MB 又在内存里挂一份，9 张就是 27MB。
 * ⚠ mock 模式下**不发任何上传请求**，直接返回签好的键（进度条会跳过上传阶段）。
 */
export async function uploadFiles(
  teamId: string,
  files: File[],
  onProgress?: (p: UploadProgress) => void,
): Promise<{ mode: 'cos' | 'mock'; objectKeys: string[]; files: File[] }> {
  const small = await compressAll(files, (done, total) =>
    onProgress?.({ phase: 'compress', ratio: done / total, done, total }),
  );
  const ext = small.map((f) => (f.name.split('.').pop() ?? 'jpg').toLowerCase());
  const signed = await signUpload(teamId, small.length, ext);

  if (signed.mode === 'cos') {
    const bytesTotal = small.reduce((a, f) => a + f.size, 0);
    // 每个文件各记各的已传字节，汇总才是整体进度
    const loaded = new Array<number>(small.length).fill(0);
    const bump = (i: number, n: number): void => {
      loaded[i] = n;
      const sum = loaded.reduce((a, b) => a + b, 0);
      onProgress?.({
        phase: 'upload',
        // ⚠ 夹到 1：XHR 的 loaded 含请求头开销，偶尔会略大于 file.size
        ratio: bytesTotal ? Math.min(1, sum / bytesTotal) : 1,
        done: sum,
        total: bytesTotal,
      });
    };
    onProgress?.({ phase: 'upload', ratio: 0, done: 0, total: bytesTotal });
    await Promise.all(
      signed.items.map((it, i) => putWithProgress(it.uploadUrl, small[i] as File, (n) => bump(i, n))),
    );
  }
  return { mode: signed.mode, objectKeys: signed.items.map((it) => it.objectKey), files: small };
}
